import React, { useMemo, useState } from 'react';
import { AlertTriangle, Info, UserPlus, X } from 'lucide-react';
import { PERMISSION_ROLES } from '../../lib/employeeAdmin';

/**
 * Add-employee form. Creates a Firebase Auth login and the matching `users`
 * profile in one step; the new person sets their own password from the reset
 * email, so no password is ever typed or shown here.
 */
export default function AddEmployeeModal({ isOpen, onClose, users, onCreate }) {
  const [form, setForm] = useState({ name: '', email: '', role: '', managerEmail: '' });
  const [allowMissingManager, setAllowMissingManager] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  /** Presets plus every role already in use, so the list never goes stale. */
  const roleOptions = useMemo(() => {
    const existing = (users || []).map(u => u.role).filter(Boolean);
    return [...new Set([...PERMISSION_ROLES, ...existing])].sort();
  }, [users]);

  const managerEmails = useMemo(
    () => [...new Set((users || []).map(u => u.email).filter(Boolean))].sort(),
    [users]
  );

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const cleanEmail = form.email.trim().toLowerCase();
  const cleanManagerEmail = form.managerEmail.trim().toLowerCase();

  const duplicate = cleanEmail
    ? (users || []).find(u => (u.email || '').toLowerCase() === cleanEmail)
    : null;
  const manager = cleanManagerEmail
    ? (users || []).find(u => (u.email || '').toLowerCase() === cleanManagerEmail)
    : null;
  const managerUnresolved = !!cleanManagerEmail && !manager;

  const isPermissionRole = PERMISSION_ROLES.includes(form.role.trim());
  const canSubmit = form.name.trim() && cleanEmail && form.role.trim() && !duplicate && !isBusy
    && (!managerUnresolved || allowMissingManager);

  const reset = () => {
    setForm({ name: '', email: '', role: '', managerEmail: '' });
    setAllowMissingManager(false);
    setError('');
    setSuccess('');
  };

  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsBusy(true);
    setError('');
    setSuccess('');

    const result = await onCreate({
      name: form.name.trim(),
      email: cleanEmail,
      role: form.role.trim(),
      managerEmail: cleanManagerEmail || undefined,
    });

    setIsBusy(false);
    if (result?.ok) {
      const notes = {
        'reset-email-failed': ' The password-reset email could not be sent — they will need a reset link.',
        'manager-link-failed': ' Their manager\'s direct-reports list could not be updated, so fix that before evaluations start.',
      };
      setSuccess(`${form.name.trim()} was added.${notes[result.warning] || ' A password-reset email is on its way to them.'}`);
      setForm({ name: '', email: '', role: '', managerEmail: '' });
      setAllowMissingManager(false);
    } else {
      setError(result?.message || 'Could not add the user.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
              <UserPlus size={18} className="text-blue-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Add employee</h2>
              <p className="text-xs text-gray-500">Creates their login and their profile</p>
            </div>
          </div>
          <button type="button" onClick={close} className="text-gray-400 hover:text-gray-600 p-1">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>
          )}
          {success && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-lg px-3 py-2">{success}</div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Full name</label>
            <input
              type="text" value={form.name} onChange={set('name')} required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="Priya Sharma"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Work email</label>
            <input
              type="email" value={form.email} onChange={set('email')} required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="priya@ruskmedia.com"
            />
            {duplicate && (
              <p className="text-[11px] text-red-600 mt-1">
                A profile already exists for this address ({duplicate.name || duplicate.email}).
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Role or job title</label>
            <input
              type="text" value={form.role} onChange={set('role')} required list="apex-role-presets"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="Senior Associate, Product"
            />
            <datalist id="apex-role-presets">
              {roleOptions.map(r => <option key={r} value={r} />)}
            </datalist>
            {isPermissionRole ? (
              <p className="flex items-start gap-1.5 text-[11px] text-blue-700 mt-1">
                <Info size={12} className="mt-0.5 flex-shrink-0" />
                <span><span className="font-semibold">{form.role.trim()}</span> is a permission level — it grants elevated access, not just a title.</span>
              </p>
            ) : (
              <p className="text-[11px] text-gray-400 mt-1">
                Anything other than {PERMISSION_ROLES.join(', ')} is treated as a job title with standard access.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Manager email <span className="font-normal text-gray-400">— needed for evaluations</span>
            </label>
            <input
              type="email" value={form.managerEmail} onChange={set('managerEmail')} list="apex-manager-emails"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="manager@ruskmedia.com"
            />
            <datalist id="apex-manager-emails">
              {managerEmails.map(m => <option key={m} value={m} />)}
            </datalist>
            {manager && (
              <p className="text-[11px] text-emerald-700 mt-1">
                Linked to {manager.name || manager.email} — they will be able to review this person.
              </p>
            )}
            {managerUnresolved && (
              <div className="mt-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                <p className="flex items-start gap-1.5 text-[11px] text-amber-800">
                  <AlertTriangle size={12} className="mt-0.5 flex-shrink-0 text-amber-600" />
                  <span>
                    No user matches that address, so no manager ID can be stored and nobody
                    will be able to review this person&rsquo;s evaluations.
                  </span>
                </p>
                <label className="flex items-center gap-2 mt-1.5 text-[11px] text-amber-900">
                  <input type="checkbox" checked={allowMissingManager} onChange={(e) => setAllowMissingManager(e.target.checked)} />
                  Add them anyway
                </label>
              </div>
            )}
            {!cleanManagerEmail && (
              <p className="text-[11px] text-gray-400 mt-1">Leave blank to set a manager later.</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={close}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Close
            </button>
            <button type="submit" disabled={!canSubmit}
              className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {isBusy ? 'Adding…' : 'Add employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
